import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { PlusCircle, Edit2, Trash2, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast, { Toaster } from 'react-hot-toast';

const ClassManagement = () => {
  const { api, user } = useAuth();
  const { t } = useTranslation('classmanagement');
  const [classes, setClasses] = useState([]);
  const [academicYears, setAcademicYears] = useState([])
  const [showAddClassModal, setShowAddClassModal] = useState(false);
  const [showAddStreamModal, setShowAddStreamModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedStream, setSelectedStream] = useState(null);
  const [formData, setFormData] = useState({ name: '', streams: [''], academic_year_id: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchClasses();
    fetchYears()
  }, []);

  const fetchClasses = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/api/v1/admin/schools/${user.school_reg}/classes`);
      setClasses(response.data);
    } catch (err) {
      setError(t('alerts.fetchFailed'));
      toast.error(t('alerts.fetchFailed'), {
        position: 'top-center'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchYears = async () =>
  {
    try
    {
      setIsLoading(true);
      const response = await api.get(`/api/v1/academic-years/`);
      setAcademicYears(response.data);
    }
    catch (err) {
      setError(t('alerts.fetchFailed'));
      toast.error(t('alerts.fetchFailed'), {
        position: 'top-center'
      });
    } finally {
      setIsLoading(false);
    }
  }

  const handleAddClass = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const classResponse = await api.post(`/api/v1/admin/${user.school_reg}/classes`, {
        name: formData.name, academic_year_id: parseInt(formData.academic_year_id)
      });

      const newClassId = classResponse.data.id;
      await Promise.all(
        formData.streams.filter(stream => stream).map(stream =>
          api.post(`/api/v1/admin/schools/${user.school_reg}/classes/${newClassId}/streams`, {
            name: stream
          })
        )
      );

      await fetchClasses();
      setShowAddClassModal(false);
      setFormData({ name: '', streams: [''] });
      toast.success(t('alerts.addClassSuccess'), {
        position: 'top-center'
      });
    } catch (err) {
      setError(t('alerts.addClassFailed'));
      toast.error(t('alerts.addClassFailed'), {
        position: 'top-center'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddStream = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      await api.post(
        `/api/v1/admin/schools/${user.school_reg}/classes/${selectedClass.id}/streams`,
        { name: formData.name }
      );
      await fetchClasses();
      setShowAddStreamModal(false);
      setFormData({ name: '', streams: [''] });
      toast.success(t('alerts.addStreamSuccess'), {
        position: 'top-center'
      });
    } catch (err) {
      setError(t('alerts.addStreamFailed'));
      toast.error(t('alerts.addStreamFailed'), {
        position: 'top-center'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      if (selectedStream) {
        await api.patch(
          `/api/v1/admin/schools/${user.school_reg}/classes/${selectedClass.id}/streams/${selectedStream.id}`,
          { name: formData.name }
        );
      } else {
        await api.patch(
          `/api/v1/admin/schools/${user.school_reg}/classes/${selectedClass.id}`,
          { name: formData.name }
        );
      }
      await fetchClasses();
      setShowEditModal(false);
      setFormData({ name: '', streams: [''] });
      toast.success(t('alerts.updateSuccess'), {
        position: 'top-center'
      });
    } catch (err) {
      setError(t('alerts.updateFailed'));
      toast.error(t('alerts.updateFailed'), {
        position: 'top-center'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsLoading(true);
      if (selectedStream) {
        await api.delete(
          `/api/v1/admin/schools/${user.school_reg}/classes/${selectedClass.id}/streams/${selectedStream.id}`
        );
      } else {
        await api.delete(
          `/api/v1/admin/schools/${user.school_reg}/classes/${selectedClass.id}`
        );
      }
      await fetchClasses();
      setShowDeleteModal(false);
      setSelectedStream(null);
      toast.success(t('alerts.deleteSuccess'), {
        position: 'top-center'
      });
    } catch (err) {
      setError(t('alerts.deleteFailed'));
      toast.error(t('alerts.deleteFailed'), {
        position: 'top-center'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addStreamField = () => {
    setFormData(prev => ({
      ...prev,
      streams: [...prev.streams, '']
    }));
  };

  const rtlClass = document.documentElement.dir === 'rtl' ? 'rtl text-right' : '';

  return (
    <div className={`p-4 ${rtlClass}`}>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            borderRadius: '10px',
            background: '#333',
            color: '#fff',
          },
        }}
      />
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800">
          {t('pageTitle')}
        </h1>
        <button
          onClick={() => setShowAddClassModal(true)}
          className="w-full sm:w-auto flex items-center justify-center gap-2 bg-[#0E5252] hover:bg-[#106363] text-white px-4 py-2 rounded-lg transition-colors"
        >
          <PlusCircle className="w-5 h-5" />
          <span className="whitespace-nowrap">{t('buttons.addClass')}</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Classes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {classes.map((classItem) => (
          <div
            key={classItem.id}
            className="bg-white rounded-lg shadow-md p-4 sm:p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800">{classItem.name}</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setSelectedClass(classItem);
                    setFormData({ name: classItem.name, streams: [''] });
                    setShowEditModal(true);
                  }}
                  className="text-gray-600 hover:text-blue-600"
                >
                  <Edit2 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    setSelectedClass(classItem);
                    setSelectedStream(null);
                    setShowDeleteModal(true);
                  }}
                  className="text-gray-600 hover:text-red-600"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Streams List */}
            <div className="space-y-2">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-medium text-gray-600">{t('labels.streams')}</h3>
                <button
                  onClick={() => {
                    setSelectedClass(classItem);
                    setShowAddStreamModal(true);
                  }}
                  className="text-blue-600 hover:text-blue-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              {classItem.streams.length > 0 ? (
                <div className="space-y-2">
                  {classItem.streams.map((stream) => (
                    <div
                      key={stream.id}
                      className="flex justify-between items-center bg-gray-50 p-2 rounded"
                    >
                      <span className="text-sm text-gray-700">{stream.name}</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setSelectedClass(classItem);
                            setSelectedStream(stream);
                            setFormData({ name: stream.name, streams: [''] });
                            setShowEditModal(true);
                          }}
                          className="text-gray-600 hover:text-blue-600"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedClass(classItem);
                            setSelectedStream(stream);
                            setShowDeleteModal(true);
                          }}
                          className="text-gray-600 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">{t('labels.noStreams')}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add Class Modal */}
      {showAddClassModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className={`bg-white rounded-lg p-6 w-full max-w-md ${rtlClass}`}>
            <h2 className="text-xl font-bold mb-4">{t('modals.addClass.title')}</h2>
            <form onSubmit={handleAddClass}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('labels.className')}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className='mb-4'>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('Academic year')}</label>
                <select value={formData.academic_year_id} className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500" onChange={e => setFormData({...formData, academic_year_id: e.target.value})} required>
                  <option value={""}>Select academic year</option>
                  {
                    academicYears.map(year =>
                    {
                      return(
                        <option key={year.id} value={year.id}>{year.year_name}</option>
                      )
                    }
                    )
                  }
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('labels.streams')}
                </label>
                {formData.streams.map((stream, index) => (
                  <input
                    key={index}
                    type="text"
                    value={stream}
                    onChange={(e) => {
                      const newStreams = [...formData.streams];
                      newStreams[index] = e.target.value;
                      setFormData({ ...formData, streams: newStreams });
                    }}
                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 mb-2"
                    placeholder={t('placeholders.stream', { number: index + 1 })}
                  />
                ))}
                <button
                  type="button"
                  onClick={addStreamField}
                  className="text-blue-600 hover:text-blue-700 text-sm"
                >
                  {t('labels.addAnotherStream')}
                </button>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddClassModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-700"
                >
                  {t('buttons.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 text-white rounded bg-[#0E5252] hover:bg-[#106363]"
                >
                  {isLoading ? t('buttons.adding') : t('buttons.addClass')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Stream Modal */}
      {showAddStreamModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className={`bg-white rounded-lg p-6 w-full max-w-md ${rtlClass}`}>
            <h2 className="text-xl font-bold mb-4">{t('modals.addStream.title')}</h2>
            <form onSubmit={handleAddStream}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('labels.streamName')}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddStreamModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-700"
                >
                  {t('buttons.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 bg-[#0E5252] hover:bg-[#106363] text-white rounded"
                >
                  {isLoading ? t('buttons.adding') : t('buttons.addStream')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className={`bg-white rounded-lg p-6 w-full max-w-md ${rtlClass}`}>
            <h2 className="text-xl font-bold mb-4">
              {selectedStream ? t('modals.edit.titleStream') : t('modals.edit.titleClass')}
            </h2>
            <form onSubmit={handleEdit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('labels.name')}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedStream(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-700"
                >
                  {t('buttons.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 bg-[#0E5252] hover:bg-[#106363] text-white rounded"
                >
                  {isLoading ? t('buttons.saving') : t('buttons.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className={`bg-white rounded-lg p-6 w-full max-w-md ${rtlClass}`}>
            <h2 className="text-xl font-bold mb-4">{t('modals.delete.title')}</h2>
            <p className="text-gray-600 mb-4">
              {selectedStream 
                ? t('modals.delete.messageStream', { streamName: selectedStream.name })
                : t('modals.delete.messageClass', { className: selectedClass.name })}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedStream(null);
                }}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                {t('buttons.cancel')}
              </button>
              <button
                onClick={handleDelete}
                disabled={isLoading}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                {isLoading ? t('buttons.deleting') : t('buttons.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassManagement;